<template>
  <div id="app">
    <modal-component />
    <navbar-component />
    <router-view />
    <footer-component />
    <div class="buttons">
      <button v-if="isDevelopment" class="btn back-btn" @click="goToPrevious" :title="`Previous: ${getPreviousRoute()}`">← Back</button>
      <div v-if="isDevelopment" class="route-info">
        {{ getCurrentRouteIndex() }} / {{ getTotalRoutes() }}
        <button v-if="!campaignStore.activeCampaign" @click="setTestCampaign" class="test-campaign-btn" title="Set test campaign to access more routes">+Campaign</button>
        <span v-else class="campaign-active">✓ {{ campaignStore.activeCampaign.domain }}</span>
      </div>
      <button v-if="isDevelopment" class="btn next-btn" @click="goToNext" :title="`Next: ${getNextRoute()}`">Next →</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, inject } from 'vue';
import ModalComponent from '@/components/ModalComponent.vue';
import NavbarComponent from '@/components/NavbarComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import { useCampaignStore } from '@/stores';
import { useRouter, useRoute  } from 'vue-router';
const router = useRouter();
const route = useRoute();
const auth0 = inject('auth')
const campaignStore = useCampaignStore();

const isDevelopment = import.meta.env.DEV;

// Routes that don't require a campaign (campaign parameter is optional)
const routesWithoutCampaign = [
  '/',
  '/error-page',
  '/profile'
];

// Routes that require an active campaign
const routesWithCampaign = [
  '/receipt-upload',
  '/iban',
  '/thank-you',
  '/campaign-is-over',
  '/faq',
  '/product-upload',
  '/product-reupload',
  '/receipt-reupload',
  '/iban-reupload',
  '/iban-thank-you',
  '/Magazine',
  '/reupload-thank-you',
  '/expired-page',
  '/terms'
];

// Get available routes based on current state
const getAvailableRoutes = () => {
  let availableRoutes = [...routesWithoutCampaign];
  
  // If we have an active campaign, add campaign-specific routes
  if (campaignStore.activeCampaign) {
    const campaignDomain = campaignStore.activeCampaign.domain || campaignStore.activeCampaign.id;
    availableRoutes.push(`/${campaignDomain}`);
    availableRoutes.push(...routesWithCampaign.map(route => `/${campaignDomain}${route}`));
  }
  
  return availableRoutes;
};

const goToNext = () => {
  const availableRoutes = getAvailableRoutes();
  const currentPath = route.path;
  const currentIndex = availableRoutes.indexOf(currentPath);
  
  if (currentIndex === -1) {
    // If current route not in list, go to first route
    router.push(availableRoutes[0]);
  } else {
    const nextIndex = (currentIndex + 1) % availableRoutes.length;
    router.push(availableRoutes[nextIndex]);
  }
};

const goToPrevious = () => {
  const availableRoutes = getAvailableRoutes();
  const currentPath = route.path;
  const currentIndex = availableRoutes.indexOf(currentPath);
  
  if (currentIndex === -1) {
    // If current route not in list, go to last route
    router.push(availableRoutes[availableRoutes.length - 1]);
  } else {
    const previousIndex = (currentIndex - 1 + availableRoutes.length) % availableRoutes.length;
    router.push(availableRoutes[previousIndex]);
  }
};

// Helper functions for development navigation info
const getCurrentRouteIndex = () => {
  const availableRoutes = getAvailableRoutes();
  const currentIndex = availableRoutes.indexOf(route.path);
  return currentIndex === -1 ? '?' : (currentIndex + 1);
};

const getTotalRoutes = () => {
  return getAvailableRoutes().length;
};

const getNextRoute = () => {
  const availableRoutes = getAvailableRoutes();
  const currentIndex = availableRoutes.indexOf(route.path);
  if (currentIndex === -1) return availableRoutes[0];
  const nextIndex = (currentIndex + 1) % availableRoutes.length;
  return availableRoutes[nextIndex];
};

const getPreviousRoute = () => {
  const availableRoutes = getAvailableRoutes();
  const currentIndex = availableRoutes.indexOf(route.path);
  if (currentIndex === -1) return availableRoutes[availableRoutes.length - 1];
  const previousIndex = (currentIndex - 1 + availableRoutes.length) % availableRoutes.length;
  return availableRoutes[previousIndex];
};

// Development helper to set a test campaign
const setTestCampaign = () => {
  if (isDevelopment && !campaignStore.activeCampaign) {
    campaignStore.setActiveCampaign({
      id: 'test-campaign',
      domain: 'test-campaign',
      name: 'Test Campaign'
    });
  }
};

onMounted(async () => {
  const loginFlag = route.query.login;
  if (loginFlag && loginFlag === 'true' && !auth0.isAuthenticated) {
    try {
      // Wait for auth0 to be properly initialized
      await auth0.init();
      
      // Double check if user is still not authenticated after init
      if (!auth0.isAuthenticated) {
        const redirectUrl = campaignStore.activeCampaign ? `/${campaignStore.activeCampaign.domain}` : '/';
        await auth0.loginWithRedirect({
          appState: {
            targetUrl: redirectUrl,
          }
        });
      }
    } catch (error) {
      console.error('Error during auth initialization or loginWithRedirect:', error);
    }
  }
});
</script>

<style scoped lang="scss">
#app {
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  min-height: 100vh;
}

.buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  position: fixed;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 1000;
  
  button {
    background: #288566;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
    
    &:hover {
      background: #1a5f47;
    }
  }
  
  .route-info {
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-family: monospace;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .test-campaign-btn {
      background: #ffa500;
      color: black;
      border: none;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
      cursor: pointer;
      
      &:hover {
        background: #ff8c00;
      }
    }
    
    .campaign-active {
      color: #90EE90;
      font-size: 10px;
    }
  }
}
</style>
